import React, { useEffect, useRef, useState } from 'react';
import { Folder, FolderInput, Plus } from 'lucide-react';

/**
 * Всплывающее меню «Переложить в папку»: папки (текущая отмечена), «Без папки»,
 * «Новая папка…». Закрывается по клику вне меню и по Esc. Всё приходит пропсами.
 */
export default function MoveToFolderMenu({ folders, currentId, onMove, onCreateAndMove, onClose }) {
  const ref = useRef(null);
  const [creating, setCreating] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const submit = async () => {
    const name = value.trim();
    if (!name || busy) return;
    setBusy(true); setError('');
    try {
      await onCreateAndMove(name);
    } catch (err) {
      setError(err?.message || 'Не получилось. Попробуйте ещё раз');
    } finally {
      setBusy(false);
    }
  };

  const row = 'flex w-full items-center gap-2 rounded-[8px] px-2 py-[6px] text-left text-[13px] hover:bg-paper-card';

  return (
    <div
      ref={ref}
      data-testid="move-menu"
      onClick={(e) => e.stopPropagation()}
      className="z-30 w-56 rounded-panel border border-paper-line bg-paper-bg p-1 shadow-lg"
    >
      <div className="flex items-center gap-2 px-2 py-1 text-[11px] font-semibold text-paper-ink-3">
        <FolderInput className="h-3.5 w-3.5" />
        Переложить в папку
      </div>
      {folders.map((f) => (
        <button
          key={f.id}
          data-testid={`move-to-${f.id}`}
          aria-current={f.id === currentId ? 'true' : undefined}
          onClick={() => onMove(f.id)}
          className={`${row} ${f.id === currentId ? 'font-semibold text-paper-ink' : 'text-paper-ink-2'}`}
        >
          <Folder className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1 min-w-0 truncate">{f.name}</span>
          {f.id === currentId && <span aria-hidden="true">✓</span>}
        </button>
      ))}
      <button data-testid="move-to-none" onClick={() => onMove(null)} className={`${row} text-paper-ink-2`}>
        Без папки
      </button>
      {creating ? (
        <div className="px-2 py-1">
          <input
            autoFocus
            aria-label="Название папки"
            value={value}
            maxLength={64}
            placeholder="Название папки"
            onChange={(e) => { setValue(e.target.value); setError(''); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submit(); }
              else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setCreating(false); setValue(''); setError(''); }
            }}
            className="w-full min-w-0 rounded-[6px] bg-paper-card px-[6px] py-[2px] text-[13px] text-paper-ink outline-none"
          />
          {error && <div className="mt-1 text-[12px] text-paper-accent">{error}</div>}
        </div>
      ) : (
        <button data-testid="move-new" onClick={() => setCreating(true)} className={`${row} text-paper-ink-3`}>
          <Plus className="h-3.5 w-3.5 shrink-0" />
          Новая папка…
        </button>
      )}
    </div>
  );
}
